import React, { useId } from 'react'
import clsx from 'clsx'
import './ApplyV2InputRadio.scss'

type OptionValue = string | number | null | boolean

interface Option {
    label: string | number
    value: string | number | boolean
}

interface ApplyV2InputRadioProps {
    value?: OptionValue
    isRequired?: boolean
    label?: string
    className?: string
    id?: string | number
    name?: string
    isDisabled?: boolean
    errorMessage?: string | null | boolean
    isShowErrorMessage?: boolean
    onChange?: (value: OptionValue) => void
    onFocus?: React.FocusEventHandler<HTMLInputElement>
    onBlur?: React.FocusEventHandler<HTMLInputElement>
    options?: Option[]
    description?: React.ReactNode
    isLoading?: boolean
}

const baseClass = 'ApplyV2InputRadio'

function ApplyV2InputRadio({
    value = null,
    isRequired = false,
    label,
    className = '',
    id,
    name,
    isDisabled = false,
    errorMessage,
    isShowErrorMessage = true,
    onChange = () => {},
    onFocus = () => {},
    onBlur = () => {},
    options = [],
    description,
    isLoading = false,
}: ApplyV2InputRadioProps) {
    const defaultId = useId()
    const selectId = id ? id.toString() : defaultId

    return (
        <div
            // tabIndex={0}
            className={clsx(baseClass, className, {
                [`${baseClass}--disabled`]: isDisabled,
                [`${baseClass}--error`]: errorMessage,
                [`${baseClass}--loading`]: isLoading,
                [`${className}--disabled`]: className && isDisabled,
                [`${className}--error`]: className && errorMessage,
                [`${className}--loading`]: className && isLoading,
            })}
        >
            {label && (
                <label className={`${baseClass}__label`} htmlFor={name ?? selectId}>
                    {label}
                    {isRequired && '*'}
                </label>
            )}

            <div
                className={clsx(`${baseClass}__items`, {
                    [`${className}__items`]: className,
                    [`${baseClass}__items--disabled`]: isDisabled,
                    [`${baseClass}__items--loading`]: isLoading,
                    [`${className}__items--disabled`]: isDisabled && className,
                    [`${className}__items--loading`]: isLoading && className,
                })}
            >
                {options.map((option, index) => (
                    <label
                        key={`${option.value}-${index}`}
                        className={clsx(`${baseClass}__item`, {
                            [`${baseClass}__item--disabled`]: isDisabled,
                            [`${baseClass}__item--selected`]: value === option.value,
                            [`${className}__item--disabled`]: isDisabled && className,
                            [`${className}__item--selected`]: value === option.value && className,
                        })}
                    >
                        <input
                            type="radio"
                            name={name ?? selectId}
                            value={String(option.value)}
                            checked={value === option.value}
                            onChange={() => onChange(option.value)}
                            onBlur={onBlur}
                            onFocus={onFocus}
                            disabled={isDisabled}
                        />
                        <span
                            className={clsx(`${baseClass}__item-circle`, {
                                [`${baseClass}__item-circle--disabled`]: isDisabled,
                                [`${className}__item-circle--disabled`]: isDisabled && className,
                            })}
                        />
                        <span
                            className={clsx(`${baseClass}__item-label`, {
                                [`${baseClass}__item-label--disabled`]: isDisabled,
                                [`${className}__item-label--disabled`]: isDisabled && className,
                            })}
                        >
                            {option.label}
                        </span>
                    </label>
                ))}
            </div>
            {description && (
                <div
                    className={clsx(`${baseClass}__description`, {
                        [`${className}__description`]: className,
                    })}
                >
                    {description}
                </div>
            )}
            {errorMessage && isShowErrorMessage && (
                <div
                    className={clsx(`${baseClass}__error`, {
                        [`${className}__error`]: className,
                    })}
                >
                    <div
                        className={clsx(`${baseClass}__error--icon`, {
                            [`${className}__error--icon`]: className,
                        })}
                    >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <g clipPath="url(#clip0_4800_38853)">
                                <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M8.00006 1.9334C4.64954 1.9334 1.9334 4.64954 1.9334 8.00006C1.9334 11.3506 4.64954 14.0667 8.00006 14.0667C11.3506 14.0667 14.0667 11.3506 14.0667 8.00006C14.0667 4.64954 11.3506 1.9334 8.00006 1.9334ZM0.733398 8.00006C0.733398 3.9868 3.9868 0.733398 8.00006 0.733398C12.0133 0.733398 15.2667 3.9868 15.2667 8.00006C15.2667 12.0133 12.0133 15.2667 8.00006 15.2667C3.9868 15.2667 0.733398 12.0133 0.733398 8.00006ZM8.00006 9.26673C7.66869 9.26673 7.40007 8.9981 7.40007 8.66673V4.66673C7.40007 4.33536 7.66869 4.06673 8.00006 4.06673C8.33144 4.06673 8.60006 4.33536 8.60006 4.66673V8.66673C8.60006 8.9981 8.33144 9.26673 8.00006 9.26673ZM8.00006 11.3334C8.36826 11.3334 8.66673 11.0349 8.66673 10.6667C8.66673 10.2985 8.36826 10.0001 8.00006 10.0001C7.63188 10.0001 7.3334 10.2985 7.3334 10.6667C7.3334 11.0349 7.63188 11.3334 8.00006 11.3334Z"
                                    fill="#B62626"
                                />
                            </g>
                            <defs>
                                <clipPath id="clip0_4800_38853">
                                    <rect width="16" height="16" fill="white" />
                                </clipPath>
                            </defs>
                        </svg>
                    </div>
                    <div
                        className={clsx(`${baseClass}__error--text`, {
                            [`${className}__error--text`]: className,
                        })}
                    >
                        {errorMessage}
                    </div>
                </div>
            )}
        </div>
    )
}

export default ApplyV2InputRadio
